import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {RtLoadProgressService} from '../../../rt-load-progress/rt-load-progress.service';
import {SubmissionSurfaceFile} from '../../../dashboard/symbols/submission.symbols';
import {placePriorityByDerivativeType, placePriorityByScanType} from '../../../dashboard-study-papaya/symbols/dashboard-study-papaya-slices-sort.symbols';
import {DashboardPapayaImageFile, DashboardPapayaSurfaceFile} from '../../../dashboard-study-papaya/types/dashboard-study-papaya.types';
import {DashboardSeriesDerivativeType, DashboardSeriesScanClass} from '../../../dashboard-new/types/dashboard.types';
import {DashboardViewRegionMriLabel} from '../../../dashboard-view-regions/types/dashboard-view-regions.types';

@Injectable()
export class DashboardStudyLoadFilesService {
  public lastLoadedNiftiItemId = 1;
  public lastLoadedSurfaceItemId = 1;

  constructor(private loadProgressService: RtLoadProgressService) {}

  /**
   * Generate a stable unique ID for the image based on its derivative type and scan type.
   * It will be the same for each image of the same type.
   */
  public getStableUniqueId(derivativeType: DashboardSeriesDerivativeType, scanClass: DashboardSeriesScanClass): number {
    // Each derivative type and scan type has a unique weight.
    // Use it as id so that each image will appear only once in the list.
    return (placePriorityByDerivativeType[derivativeType] + placePriorityByScanType[scanClass]) * 100;
  }

  getMockNiftiData(
    scanClass: DashboardSeriesScanClass,
    derivativeType: DashboardSeriesDerivativeType,
    isUnique = false
  ): Observable<DashboardPapayaImageFile> {
    const id = isUnique ? this.getStableUniqueId(derivativeType, scanClass) : this.lastLoadedNiftiItemId++;
    const imageName = `${scanClass}-${derivativeType}`;
    const imageNameUnique = `${imageName}-${id}`;
    const url = `https://brainkeylocal.blob.core.windows.net/brainkey-media-local/artur-test/nifti/${imageName}.nii.gz`;
    // const url = `assets/custom-data/nifti/${imageName}.nii.gz`;

    return this.loadProgressService
      .addProgressObserver<ArrayBuffer>({
        method: 'GET',
        url,
        httpOptions: {
          responseType: 'arraybuffer',
        },
      })
      .pipe(
        map(buffer => {
          // Create a blob from the buffer.
          const imageFile: Partial<DashboardPapayaImageFile> = new Blob([new Uint8Array(buffer)], {type: 'application/xml'});
          imageFile.name = imageNameUnique;

          // Add metadata to the blob.
          imageFile.metadata = {
            id,
            name: imageNameUnique,
            scanClass,
            derivativeType,
            apiEndpoint: url,
          };

          // Return the updated blob.
          return imageFile as DashboardPapayaImageFile;
        })
      );
  }

  getMockSurfaceData(
    surfaceLabel?: DashboardPapayaSurfaceFile['id'],
    derivativeId?: DashboardViewRegionMriLabel['surface_derivative_id']
  ): Observable<DashboardPapayaSurfaceFile> {
    surfaceLabel = surfaceLabel ? surfaceLabel : this.lastLoadedSurfaceItemId++;
    derivativeId = derivativeId ? derivativeId : this.lastLoadedSurfaceItemId++;
    const url = `assets/custom-data/gifti/${derivativeId}.gii`;

    return this.loadProgressService
      .addProgressObserver<Blob>({
        method: 'GET',
        url,
        httpOptions: {
          responseType: 'blob',
        },
      })
      .pipe(
        map(blob => {
          // Add metadata to the blob by extending the blob object.
          const surfaceFile: Partial<DashboardPapayaSurfaceFile> = blob as SubmissionSurfaceFile;
          surfaceFile.id = surfaceLabel;
          surfaceFile.name = `${surfaceLabel}.gii`;
          surfaceFile.labels = [+surfaceLabel];
          return surfaceFile as DashboardPapayaSurfaceFile;
        })
      );
  }
}
